<template>
  <nuxt-link
    v-if="href && (icon || text || $slots.default)"
    :to="href"
    :title="title || text"
    class="dn-link"
    :external="isExternal"
    :target="target"
    :no-prefetch="noPrefetch"
  >
    <slot>
      <dn-icon
        v-if="icon"
        :name="icon"
      />
      <span class="text">{{ text }}</span>
    </slot>
  </nuxt-link>
  <div
    v-else
    class="dn-link"
  >
    <slot>
      <dn-icon
        v-if="icon"
        :name="icon"
      />
      <span class="text">{{ text }}</span>
    </slot>
  </div>
</template>

<script lang="ts" setup>
import { useCommonStore } from '~/store/common';

const props = defineProps({
  text: {
    type: String,
    required: false,
    default: null
  },
  title: {
    type: String,
    required: false,
    default: null
  },
  href: {
    type: String,
    required: false,
    default: null
  },
  external: {
    type: Boolean,
    required: false,
    default: false
  },
  icon: {
    type: String,
    required: false,
    default: null
  },
  target: {
    type: String,
    required: false,
    default: null
  },
  noPrefetch: {
    type: Boolean,
    required: false,
    default: true
  }
});
const commonStore = useCommonStore();
const isExternal = computed(() => props.external || commonStore.isAppView);
</script>

<style lang="scss" scoped>
.dn-link {
  --_dn-link_display: var(--dn-link_display, inline);
  --_dn-link_font-family: var(--dn-link_font-family, var(--typography-utility-button-s-font-family));
  --_dn-link_font-size: var(--dn-link_font-size, var(--typography-utility-button-s-font-size));
  --_dn-link_color: var(--dn-link_color, var(--color-link-default, #000));
  --_dn-link_text-decoration: var(--dn-link_text-decoration, none);
  --_dn-link_color__hover: var(--dn-link_color__hover, var(--color-link-hover));
  --_dn-link_text-decoration__hover: var(--dn-link_text-decoration__hover);
  --_dn-link_color__active: var(--dn-link_color__active, var(--color-link-active));
  --_dn-link_text-decoration__active: var(--dn-link_text-decoration__hover);
  --_dn-link_line-height: var(--dn-link_line-height, var(--typography-utility-button-s-line-height));
  --dn_font-size: var(--_dn-link_font-size);

  display: var(--_dn-link_display);
  cursor: pointer;
  color: var(--_dn-link_color);
  text-underline-offset: 0.4rem;
  text-decoration: var(--_dn-link_text-decoration);
  font-family: var(--_dn-link_font-family);
  letter-spacing: var(--typography-utility-button-s-letter-spacing);
  line-height: var(--_dn-link_line-height);
  transition: all 0.4s cubic-bezier(0, 0.92, 0.32, 0.96);

  .dn-icon {
    max-width: 18px;
    width: 18px;
    stroke: var(--color-icon);
    transition: all 0.2s linear;

    path {
      stroke: var(--color-icon);
    }
  }

  &:hover {
    --_dn-link_color: var(--_dn-link_color__hover);

    .dn-icon {
      stroke: var(--_dn-link_color__hover);
    }
  }

  &:active {
    --_dn-link_color: var(--_dn-link_color__active);

    .dn-icon {
      stroke: var(--_dn-link_color__active);
    }
  }

  &.button,
  &.outline,
  &.pill {
    display: flex;
    align-items: center;
    white-space: nowrap;
    border-radius: var(--border-radius-button);
    transition: all 200ms ease-in-out;
    min-height: 32px;
    padding: 7px 14px;
    border: 1px solid;
  }

  &.pill,
  &.outline {
    .dn-icon {
      margin-right: 10px;

      svg {
        max-width: 20px;
        max-height: 5px;
      }
    }
  }

  &.outline {
    --dn-link_color: var(--dn-header-link_color, var(--color-button-secondary-default-fg));
    --color-icon: var(--color-button-secondary-default-fg);

    background-color: var(--color-button-secondary-default-bg);
    border-color: var(--color-button-secondary-default-border);

    &:focus-visible,
    &:hover,
    &:active {
      --dn-link_color__hover: var(--dn-header-link_color__hover, var(--color-button-secondary-hover-fg));
      --color-icon: var(--dn-header-link_color__hover, var(--color-button-secondary-hover-fg));

      background-color: var(--color-button-secondary-hover-bg);
    }

    &:active {
      --dn-link_color__active: var(--dn-header-link_color__active, var(--color-button-secondary-active-fg));
      --color-icon: var(--dn-header-link_color__hover, var(--color-button-secondary-active-fg));
    }

    &.on-dark {
      --color-button-secondary-default-fg: var(--color-brand-utility-50);
    }
  }

  &.pill {
    --dn-link_color: var(--dn-header-link_color, var(--color-button-secondary-default-fg));

    background-color: var(--color-button-secondary-default-bg);
    border-color: var(--color-button-secondary-default-border);
    border-radius: 24px;
    line-height: 1;

    &.tag {
      background-color: var(--color-button-tag-bg);
      border: none;

      &:focus-visible,
      &:hover {
        background-color: var(--color-button-tag-bg-hover);
      }
    }

    &:focus-visible,
    &:hover {
      --color-icon: var(--color-button-secondary-hover-fg);
      --dn-link_color__hover: var(--dn-header-link_color__hover, var(--color-button-secondary-hover-fg));

      background-color: var(--color-button-secondary-hover-bg);
    }

    &:active {
      --color-icon: var(--color-button-secondary-active-fg);
      --dn-link_color__active: var(--dn-header-link_color__active, var(--color-button-secondary-active-fg));
    }

    &.on-dark {
      --color-button-secondary-default-fg: var(--color-brand-utility-50);
    }
  }

  &.button {
    --dn_color: var(--color-button-primary-default-fg);

    background-color: var(--color-button-primary-default-bg);
    border-color: var(--color-button-primary-default-border);

    &:focus-visible,
    &:hover {
      --color-icon: var(--color-button-primary-hover-fg);

      background-color: var(--color-button-primary-hover-bg);
      color: var(--color-button-primary-hover-fg);
    }

    &.on-dark {
      color: var(--color-brand-utility-50);
    }

    &-inverted {
      &-primary {
        background-color: var(--color-button-primary-inverted-default-bg);
        color: var(--color-button-primary-inverted-default-fg);
        border-color: var(--color-button-primary-inverted-default-border);

        &:focus-visible,
        &:hover {
          --color-icon: var(--color-button-primary-inverted-hover-fg);

          background-color: var(--color-button-primary-inverted-hover-bg);
          color: var(--color-button-primary-inverted-hover-fg);
        }
      }

      &-secondary {
        background-color: var(--color-button-secondary-inverted-default-bg);
        color: var(--color-button-secondary-inverted-default-fg);
        border-color: var(--color-button-secondary-inverted-default-border);

        &:focus-visible,
        &:hover {
          --color-icon: var(--color-button-secondary-inverted-hover-fg);

          background-color: var(--color-button-secondary-inverted-hover-bg);
          color: var(--color-button-secondary-inverted-hover-fg);
        }
      }
    }
  }

  &.full-width {
    width: 100%;
    justify-content: center;
    padding: 16px 32px;
  }
}
</style>
